import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { teacherModuleAPI } from '../../services/api';
import { MonthlyReportBatchTeaching } from '../../components/Teacher/MonthlyReportSessionDetails';
import { MonthlyReportSnapshotFreshnessAlert } from '../../components/Teacher/MonthlyReportSnapshotFreshnessAlert';
import TeacherDayCheckinHistory from '../../components/Teacher/TeacherDayCheckinHistory';

const MIN_MONTH = '2026-06';

const NARRATIVE_FIELDS = [
  { key: 'teachingHighlights', label: 'Highlights from my classes', section: 'Teaching' },
  { key: 'studentProgress', label: 'Student progress', section: 'Teaching' },
  { key: 'challenges', label: 'Challenges & support needed', section: 'Teaching' },
  { key: 'nextMonthFocus', label: 'Focus for next month', section: 'Teaching' },
  { key: 'leaveRemarks', label: 'Leave & schedule remarks', section: 'Attendance' },
  { key: 'demosHighlights', label: 'Demos & trials highlights', section: 'Demos' },
  { key: 'trainingReflection', label: 'Training reflection', section: 'LMS' },
  { key: 'makeupNotes', label: 'Makeup classes notes', section: 'Makeup' },
  { key: 'additionalRemarks', label: 'Anything else to share', section: 'Other' },
];

function shiftMonth(month, delta) {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function listMonthsFrom(minMonth) {
  const out = [];
  let cur = minMonth;
  const now = new Date();
  const end = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  while (cur <= end) {
    out.push(cur);
    cur = shiftMonth(cur, 1);
  }
  return out.reverse();
}

function statusColor(status) {
  if (status === 'approved') return 'success';
  if (status === 'submitted') return 'info';
  if (status === 'rejected') return 'warning';
  return 'default';
}

function StatChip({ label, value }) {
  return (
    <Chip label={`${label}: ${value ?? 0}`} color="primary" variant="outlined" sx={{ fontWeight: 500 }} />
  );
}

export default function TeacherMonthlyReport() {
  const [searchParams, setSearchParams] = useSearchParams();
  const monthOptions = useMemo(() => listMonthsFrom(MIN_MONTH), []);
  const [month, setMonth] = useState(searchParams.get('month') || monthOptions[0] || MIN_MONTH);
  const [narrative, setNarrative] = useState({});
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [confirmRefresh, setConfirmRefresh] = useState(false);
  const queryClient = useQueryClient();

  const reportQuery = useQuery(
    ['monthlyReport', month],
    async () => {
      const res = await teacherModuleAPI.getMonthlyReport(month);
      return res.data?.data;
    },
    { enabled: !!month }
  );

  const studentReadinessQuery = useQuery(
    ['studentReportReadiness', month],
    async () => {
      const res = await teacherModuleAPI.getStudentReportReadiness({ month });
      return res.data?.data;
    },
    { enabled: !!month }
  );

  useEffect(() => {
    const n = reportQuery.data?.reportContent?.narrative;
    if (n) setNarrative({ ...n });
  }, [
    month,
    reportQuery.data?.id,
    reportQuery.data?.updatedAt,
    reportQuery.data?.reportContent?.narrative,
  ]);

  useEffect(() => {
    setSearchParams({ month }, { replace: true });
  }, [month, setSearchParams]);

  const editable = ['draft', 'rejected'].includes(reportQuery.data?.status);
  const snapshot = reportQuery.data?.generatedSnapshot || {};
  const summary = snapshot.summary || {};
  const allWarnings = reportQuery.data?.warnings?.items || [];
  const snapshotStale = Boolean(reportQuery.data?.warnings?.snapshotStale);
  // Dedicated freshness banner covers this code — avoid duplicating in "Before you submit".
  const warnings = allWarnings.filter((w) => w.code !== 'snapshot_stale');

  const saveMutation = useMutation(
    () =>
      teacherModuleAPI.updateMonthlyReport(month, {
        reportContent: { version: 1, narrative, uiPreferences: { hiddenAutoSectionIds: [] } },
      }),
    {
      onSuccess: () => {
        toast.success('Draft saved');
        queryClient.invalidateQueries(['monthlyReport', month]);
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Failed to save'),
    }
  );

  const refreshMutation = useMutation(
    () => teacherModuleAPI.regenerateMonthlyReport(month),
    {
      onSuccess: () => {
        toast.success('Report refreshed from your sessions');
        setConfirmRefresh(false);
        queryClient.invalidateQueries(['monthlyReport', month]);
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Failed to refresh'),
    }
  );

  const submitMutation = useMutation(() => teacherModuleAPI.submitMonthlyReport(month), {
    onSuccess: (res) => {
      const w = res.data?.warnings || [];
      if (w.length) toast.success(`Submitted (${w.length} note(s) for supervisor)`);
      else toast.success('Report submitted to supervisor');
      setConfirmSubmit(false);
      queryClient.invalidateQueries(['monthlyReport', month]);
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to submit'),
  });

  if (reportQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const data = reportQuery.data;
  if (!data) {
    return <Typography sx={{ p: 3 }}>Report not available.</Typography>;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Monthly Achievement Report
          </Typography>
          <Typography color="text.secondary">
            Share what you accomplished — review, edit, then submit to your supervisor.
          </Typography>
        </Box>
        <TextField
          select
          size="small"
          label="Month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          {monthOptions.map((m) => (
            <MenuItem key={m} value={m}>
              {m}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Chip label={data.status} color={statusColor(data.status)} />
      </Stack>

      <MonthlyReportSnapshotFreshnessAlert
        generatedAt={data.generatedAt}
        snapshotStale={snapshotStale}
        status={data.status}
        variant="teacher"
        onRefresh={() => setConfirmRefresh(true)}
        refreshDisabled={refreshMutation.isLoading}
      />

      {studentReadinessQuery.data && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Student assessments: {studentReadinessQuery.data.reports?.submitted || 0} /{' '}
          {studentReadinessQuery.data.eligibleStudents || 0} submitted ·{' '}
          {studentReadinessQuery.data.reports?.approved || 0} approved. Parent reports require
          approved student assessments and your achievement report approved.{' '}
          <Button size="small" href="/teacher/student-reports">
            Open student reports
          </Button>
        </Alert>
      )}

      {data.status === 'rejected' && data.rejectionReason && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography fontWeight={600}>Supervisor feedback</Typography>
          {data.rejectionReason}
        </Alert>
      )}

      {warnings.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography fontWeight={600} sx={{ mb: 1 }}>
            Before you submit
          </Typography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {warnings.map((w) => (
              <li key={w.code}>{w.message}</li>
            ))}
          </ul>
          <Typography variant="body2" sx={{ mt: 1 }}>
            You can still submit — your supervisor will see these notes.
          </Typography>
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2, bgcolor: 'primary.50' }}>
        <Typography variant="h6" gutterBottom>
          {month} — at a glance
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          <StatChip label="Classes delivered" value={summary.sessionsDelivered} />
          <StatChip label="Classes scheduled" value={summary.sessionsScheduled} />
          <StatChip label="Students taught" value={summary.uniqueStudents} />
          <StatChip label="Modules covered" value={summary.moduleTypesCovered} />
          <StatChip label="Demos" value={summary.demosConducted} />
          <StatChip label="Training completed" value={summary.trainingCompletedInMonth} />
          <StatChip label="Homework given" value={summary.homeworkGivenCount ?? summary.homeworkSentCount} />
          <StatChip label="Makeup classes" value={summary.makeupSessions} />
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Delivered = sessions on or before the refresh date (future timetable classes stay scheduled
          only).
        </Typography>
      </Paper>

      <TeacherDayCheckinHistory month={month} />

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="h6">Teaching sessions</Typography>
              <Button
                component={RouterLink}
                to={`/teacher/what-was-taught?month=${month}`}
                size="small"
                variant="outlined"
              >
                What each student was taught
              </Button>
            </Stack>
            {(snapshot.teaching?.batches || []).length === 0 ? (
              <Typography color="text.secondary">No classes recorded this month.</Typography>
            ) : (
              (snapshot.teaching?.batches || []).map((batch) => (
                <MonthlyReportBatchTeaching key={batch.batchId} batch={batch} />
              ))
            )}
          </Paper>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Leave & attendance
            </Typography>
            <Typography variant="body2">
              Approved leave days: {(snapshot.leave?.approvedDays || []).length}
            </Typography>
            <Typography variant="body2">
              Substitute sessions taught: {snapshot.leave?.substituteSessions ?? 0}
            </Typography>
          </Paper>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Demos & trials
            </Typography>
            <Typography variant="body2">
              Total: {snapshot.demos?.summary?.total ?? 0} · Completed:{' '}
              {snapshot.demos?.summary?.completed ?? 0}
            </Typography>
          </Paper>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              LMS training
            </Typography>
            <Typography variant="body2">
              Completed this month: {snapshot.lmsTraining?.completedInMonth?.length ?? 0} · Overall
              completed: {snapshot.lmsTraining?.completedTotal ?? 0} /{' '}
              {snapshot.lmsTraining?.assignedTotal ?? 0}
            </Typography>
          </Paper>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Makeup classes
            </Typography>
            <Typography variant="body2">
              Sessions: {snapshot.makeup?.sessions?.length ?? 0} · Students:{' '}
              {snapshot.makeup?.studentCount ?? 0}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2, position: { md: 'sticky' }, top: 16 }}>
            <Typography variant="h6" gutterBottom>
              Your narrative (optional)
            </Typography>
            {NARRATIVE_FIELDS.map((field) => (
              <TextField
                key={field.key}
                fullWidth
                multiline
                minRows={2}
                margin="dense"
                label={`${field.section}: ${field.label}`}
                value={narrative[field.key] || ''}
                onChange={(e) => setNarrative((prev) => ({ ...prev, [field.key]: e.target.value }))}
                disabled={!editable}
              />
            ))}
          </Paper>
        </Grid>
      </Grid>

      {editable && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 2 }}>
          <Button variant="outlined" onClick={() => saveMutation.mutate()} disabled={saveMutation.isLoading}>
            Save draft
          </Button>
          <Button variant="outlined" onClick={() => setConfirmRefresh(true)} disabled={refreshMutation.isLoading}>
            Refresh from sessions
          </Button>
          <Button variant="contained" onClick={() => setConfirmSubmit(true)} disabled={submitMutation.isLoading}>
            Submit to supervisor
          </Button>
        </Stack>
      )}

      {data.status === 'approved' && (
        <Alert severity="success" sx={{ mt: 2 }}>
          This report was approved
          {data.reviewedAt ? ` on ${new Date(data.reviewedAt).toLocaleDateString()}` : ''}.
        </Alert>
      )}

      <Dialog open={confirmSubmit} onClose={() => setConfirmSubmit(false)}>
        <DialogTitle>Submit monthly report?</DialogTitle>
        <DialogContent>
          <Typography>
            Your supervisor will review and approve or return the report with feedback.
          </Typography>
          {warnings.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {warnings.length} note(s) will be visible to your supervisor.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSubmit(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => submitMutation.mutate()}
            disabled={submitMutation.isLoading}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmRefresh} onClose={() => setConfirmRefresh(false)}>
        <DialogTitle>Refresh from sessions?</DialogTitle>
        <DialogContent>
          <Typography>
            Updates class stats, modules, demos, and training from your session data. Your written notes
            will not change.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRefresh(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => refreshMutation.mutate()}>
            Refresh
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
